<template>
  <div class="ivr-blacklist-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR Blacklist" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>Blacklist</h1>
            <p class="subtitle">Numbers blocked from reaching your IVR flows.</p>
          </div>
          <button class="create-btn" @click="showAddModal = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Add Number
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          {{ error }}
        </div>

        <!-- Table -->
        <div v-else class="table-card">
          <table class="data-table" v-if="blacklist.length">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Reason</th>
                <th>Expires</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in blacklist" :key="item.id">
                <td>
                  <span class="phone-number">{{ item.phone_number }}</span>
                </td>
                <td class="muted-cell">{{ item.reason || '—' }}</td>
                <td>
                  <span v-if="item.expires_at" class="expires-text">{{ formatDate(item.expires_at) }}</span>
                  <span v-else class="permanent-badge">Permanent</span>
                </td>
                <td class="muted-cell">{{ formatDate(item.created_at) }}</td>
                <td>
                  <button class="action-btn action-btn-red" title="Remove from blacklist" @click="confirmRemove(item)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 20c-4.962 0-9-4.038-9-9s4.038-9 9-9 9 4.038 9 9-4.038 9-9 9zm0-14a1 1 0 0 0-1 1v5a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1zm0 10a1.25 1.25 0 1 0 0-2.5A1.25 1.25 0 0 0 12 17z"/></svg>
            </div>
            <h3>No blacklisted numbers</h3>
            <p>Add a number to block it from reaching your IVR flows.</p>
          </div>
        </div>

        <!-- Add Modal -->
        <div v-if="showAddModal" class="modal-overlay" @click.self="showAddModal = false">
          <div class="modal-container">
            <div class="modal-header">
              <h2>Add to Blacklist</h2>
              <button class="modal-close" @click="showAddModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label">Phone Number *</label>
                <input v-model="addForm.phone_number" class="form-input" placeholder="e.g. 0201000000000" />
              </div>
              <div class="form-group">
                <label class="form-label">Reason</label>
                <input v-model="addForm.reason" class="form-input" placeholder="e.g. Repeated spam" />
              </div>
              <div class="form-group">
                <label class="form-label">Expires At <span class="label-hint">(leave blank for permanent)</span></label>
                <input v-model="addForm.expires_at" type="datetime-local" class="form-input" />
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="showAddModal = false">Cancel</button>
              <button class="btn-primary" :disabled="adding || !addForm.phone_number" @click="addNumber">
                {{ adding ? 'Adding...' : 'Add to Blacklist' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Remove Confirm Modal -->
        <div v-if="removeTarget" class="modal-overlay" @click.self="removeTarget = null">
          <div class="modal-container modal-sm">
            <div class="modal-header">
              <h2>Remove from Blacklist</h2>
              <button class="modal-close" @click="removeTarget = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="confirm-text">Remove <strong>{{ removeTarget.phone_number }}</strong> from the blacklist?</p>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="removeTarget = null">Cancel</button>
              <button class="btn-danger" :disabled="removing" @click="doRemove">{{ removing ? 'Removing...' : 'Remove' }}</button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'IVRBlacklistView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      blacklist: [],
      loading: true,
      error: null,
      showAddModal: false,
      adding: false,
      addForm: { phone_number: '', reason: '', expires_at: '' },
      removeTarget: null,
      removing: false
    }
  },
  async mounted () { await this.load() },
  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const res = await IVRService.getBlacklist()
        this.blacklist = res.blacklist || []
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    async addNumber () {
      if (!this.addForm.phone_number) return
      this.adding = true
      try {
        const payload = {
          phone_number: this.addForm.phone_number,
          reason: this.addForm.reason || undefined,
          expires_at: this.addForm.expires_at || undefined
        }
        await IVRService.addToBlacklist(payload)
        this.showAddModal = false
        this.addForm = { phone_number: '', reason: '', expires_at: '' }
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.adding = false
      }
    },

    confirmRemove (item) { this.removeTarget = item },

    async doRemove () {
      this.removing = true
      try {
        await IVRService.removeFromBlacklist(this.removeTarget.id)
        this.removeTarget = null
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.removing = false
      }
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    }
  }
}
</script>

<style scoped>
.ivr-blacklist-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}
.create-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.create-btn:hover {
  background: #2563EB;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59,130,246,0.4);
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 16px 20px;
  color: #DC2626;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: var(--text-secondary);
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}
.empty-state h3 { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; margin: 0; }

/* Table */
.table-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}
.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: var(--bg-tertiary);
  padding: 12px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--border-primary);
}
.data-table td {
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
  font-size: 14px;
  color: var(--text-primary);
}
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover { background: var(--bg-tertiary); }

.phone-number {
  font-family: monospace;
  font-weight: 700;
  font-size: 14px;
  color: var(--text-primary);
}
.muted-cell { color: var(--text-secondary); font-size: 13px; }

.expires-text { font-size: 13px; color: var(--text-secondary); }
.permanent-badge {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: #FEE2E2;
  color: #DC2626;
}

/* Action Button */
.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.action-btn-red { color: #EF4444; }
.action-btn-red:hover { background: #FEF2F2; border-color: #FECACA; color: #DC2626; }

/* Buttons */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; transform: translateY(-1px); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover { background: var(--bg-tertiary); }

.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-sm { max-width: 400px; }
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.modal-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.modal-body { padding: 20px 24px; }
.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.form-group { margin-bottom: 16px; }
.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.label-hint {
  font-weight: 400;
  color: var(--text-secondary);
}
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 15px;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.form-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.confirm-text {
  font-size: 15px;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.5;
}
</style>
